import { useRouter } from 'next/router';
import { ReactElement, useEffect, useMemo, useRef } from 'react';

import CopyAffiliateLinkButton from '@components/affiliate/CopyAffiliateLinkButton';
import LandingPageFooter from '@components/shared/LandingPageFooter';
import { UIPaths } from '@consts';
import { GoalModel, GoalSellingStatus, isAcquiredViaEnroll } from '@models/goals';
import { GoalSubscriptionStatus } from '@models/goalSubscriptions';
import AnimatedSection from '../../animations/AnimatedSection';

import { PaymentAndOrderProvider } from '../../payments/PaymentAndOrderContext';
import GoalLandingPageDiscordBanner from './GoalLandingPageDiscordBanner';
import GoalLandingPageEnrollBanner from './GoalLandingPageEnrollBanner';
import GoalLandingPageFAQSection from './GoalLandingPageFAQSection';
import GoalLandingPageHeroSection from './GoalLandingPageHeroSection';
import GoalLandingPageKeywordsSection from './GoalLandingPageKeywordsSection';
import GoalLandingPageMentorBioSection from './GoalLandingPageMentorBioSection';
import GoalLandingPageModulesSection from './GoalLandingPageModulesSection';
import GoalLandingPageOverviewSection from './GoalLandingPageOverviewSection';
import GoalLandingPagePreviewVideoSection from './GoalLandingPagePreviewVideoSection';
import GoalLandingPagePricingSection from './GoalLandingPagePricingSection';
import GoalLandingPageRecommendations from './GoalLandingPageRecommendations';
import GoalLandingPageSellingStatusBanner from './GoalLandingPageSellingStatusBanner';
import GoalLandingPageSocialProofSection from './GoalLandingPageSocialProofSection';
import GoalLandingPageTestimonialsSection from './GoalLandingPageTestimonialsSection';

export function GoalLandingPage({ goal }: { goal: GoalModel }): ReactElement {
  const router = useRouter();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Fragment (#pricing / #pricing-cta) or legacy ?scrollTo= — skip pricing entry animation so scroll target layout is stable.
  const landingScrollTarget = useMemo(() => {
    if (typeof window !== 'undefined') {
      const h = window.location.hash.slice(1);
      if (h === 'pricing' || h === 'pricing-cta' || h === 'pricing-card') return h;
      const fromSearch = new URLSearchParams(window.location.search).get('scrollTo');
      if (fromSearch === 'pricingCta') return 'pricing-cta';
      if (fromSearch === 'pricing') return 'pricing';
    }
    const raw = router.query.scrollTo;
    const v = Array.isArray(raw) ? raw[0] : raw;
    if (v === 'pricingCta') return 'pricing-cta';
    if (v === 'pricing') return 'pricing';
    return null;
  }, [router.asPath, router.query.scrollTo]);
  const isSubscribed = useMemo(
    () => goal?.subscription?.status === GoalSubscriptionStatus.ACTIVE,
    [goal?.subscription]
  );
  const canEnroll = useMemo(() => !!goal && isAcquiredViaEnroll(goal), [goal?.enrollSpec]);
  const isPreSale = useMemo(
    () => goal?.sellingStatus === GoalSellingStatus.PRE_SALE,
    [goal?.sellingStatus]
  );
  const isSoldOut = useMemo(
    () => goal?.sellingStatus === GoalSellingStatus.SOLD_OUT,
    [goal?.sellingStatus]
  );
  const showEnrollBanner = useMemo(() => canEnroll && !isSubscribed, [canEnroll, isSubscribed]);
  const showSellingStatusBanner = useMemo(() => isPreSale || isSoldOut, [isPreSale, isSoldOut]);

  // Reset scroll position when goal changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [goal?.entityId]);

  // Scroll to #pricing / #pricing-cta (or legacy ?scrollTo=), then normalize URL
  useEffect(() => {
    if (!router.isReady) return;

    const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
    const targetFromHash =
      hash === 'pricing' || hash === 'pricing-cta' || hash === 'pricing-card' ? hash : null;

    const scrollToRaw = router.query.scrollTo;
    const scrollToParam = Array.isArray(scrollToRaw) ? scrollToRaw[0] : scrollToRaw;
    const targetFromQuery =
      scrollToParam === 'pricingCta'
        ? 'pricing-cta'
        : scrollToParam === 'pricing'
          ? 'pricing'
          : null;

    const targetId = targetFromHash ?? targetFromQuery;

    if (!targetId || !scrollContainerRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      const targetElement = document.getElementById(targetId);
      if (!targetElement) return;

      const isNarrow = window.matchMedia('(max-width: 639px)').matches;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      targetElement.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: isNarrow ? 'center' : 'start',
        inline: 'nearest'
      });

      if (typeof window !== 'undefined' && window.location.hash) {
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}${window.location.search}`
        );
      }

      if (router.query.scrollTo !== undefined) {
        const { scrollTo: _scrollTo, ...restQuery } = router.query;
        router.replace(
          {
            pathname: router.pathname,
            query: restQuery
          },
          undefined,
          { shallow: true }
        );
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [router.isReady, router.asPath, router.query, goal?.entityId]);

  return (
    <PaymentAndOrderProvider goal={goal}>
      <div
        ref={scrollContainerRef}
        // Container-queries apply to descendants; this element's own breakpoint classes
        // must stay viewport-keyed (`sm:`) because it isn't inside an outer @container.
        className='@container select-text w-full h-dvh overflow-y-auto scroll-smooth'
      >
        <CopyAffiliateLinkButton
          path={UIPaths.goal(goal?.entityId)}
          isGoalAuthor={goal?.user?.isMe === true}
        />
        {/* BANNER START */}
        {showEnrollBanner && <GoalLandingPageEnrollBanner />}
        {showSellingStatusBanner && !showEnrollBanner && (
          <GoalLandingPageSellingStatusBanner
            sellingStatus={goal?.sellingStatus}
            withSubtitle={false}
          />
        )}
        {/* BANNER END */}

        <GoalLandingPageHeroSection />

        <AnimatedSection>
          <GoalLandingPagePreviewVideoSection />
        </AnimatedSection>

        <AnimatedSection>
          <GoalLandingPageOverviewSection />
        </AnimatedSection>

        <AnimatedSection>
          <GoalLandingPageKeywordsSection />
        </AnimatedSection>

        <GoalLandingPageDiscordBanner />

        <AnimatedSection>
          <GoalLandingPageMentorBioSection />
        </AnimatedSection>

        {/* BANNER */}
        {showEnrollBanner && <GoalLandingPageEnrollBanner />}
        {showSellingStatusBanner && !showEnrollBanner && (
          <GoalLandingPageSellingStatusBanner
            sellingStatus={goal?.sellingStatus}
            withSubtitle={true}
          />
        )}
        {/* BANNER END */}

        {!isSoldOut && (
          <AnimatedSection>
            <GoalLandingPageSocialProofSection />
          </AnimatedSection>
        )}

        {!isSoldOut && (
          <AnimatedSection>
            <GoalLandingPageTestimonialsSection />
          </AnimatedSection>
        )}

        {!isSoldOut && (
          <AnimatedSection>
            <GoalLandingPageModulesSection />
          </AnimatedSection>
        )}

        {/* BANNER START */}
        {showEnrollBanner && !isSoldOut && <GoalLandingPageEnrollBanner />}
        {showSellingStatusBanner && !showEnrollBanner && !isSoldOut && (
          <GoalLandingPageSellingStatusBanner
            sellingStatus={goal?.sellingStatus}
            withSubtitle={false}
          />
        )}
        {/* BANNER END */}

        {!isSoldOut && (
          <AnimatedSection disableEntryAnimation={!!landingScrollTarget}>
            <GoalLandingPagePricingSection />
          </AnimatedSection>
        )}

        {!isSoldOut && (
          <AnimatedSection>
            <GoalLandingPageFAQSection />
          </AnimatedSection>
        )}

        <AnimatedSection>
          <GoalLandingPageRecommendations />
        </AnimatedSection>

        <LandingPageFooter />
      </div>
    </PaymentAndOrderProvider>
  );
}

export default GoalLandingPage;
